import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useInstalledEquipmentQuery } from '@/api/appSolutionBuilder/filterUnit';
import type { ProductItemResponse } from '@/api/product/types';
import { AutoComplete, InputText } from '@/components/shared';
import { SegmentationContext } from '@/contexts/SegmentationContext';
import type { SparePartRegistrationResponse } from '@/generated/data.schemas';
import { useGetSettings } from '@/generated/settings';
import { useGetTranslations } from '@/generated/translations';
import { useDebounce } from '@/helpers';
import { getDateString } from '@/helpers/dateHelpers';
import { getArticleName } from '@/helpers/getArticleName';
import type { SelectItem } from '@/types';
import styles from '../SparePart.module.scss';

interface SparePartFormProps {
	formId: 'edit-sparePart-form' | 'register-sparePart-form';
	legend?: string;
	sparePart?: SparePartRegistrationResponse;
	validationErrors: Record<string, string[]> | null;
	productItems?: ProductItemResponse[] | null;
	handleSubmit: (e) => void;
	handleChange: () => void;
}

export const SparePartForm: React.FunctionComponent<SparePartFormProps> = ({
	formId,
	legend,
	sparePart,
	validationErrors,
	productItems,
	handleSubmit,
	handleChange,
}) => {
	const { segmentationId } = useContext(SegmentationContext);
	const { data: translations } = useGetTranslations(segmentationId);
	const { data: settings } = useGetSettings(segmentationId);

	const initialMount = useRef(true);

	const [forceClose, setForceClose] = useState<boolean>(false);
	const [articleNumber, setArticleNumber] = useState<string>(sparePart?.articleNumber || '');
	const [sparePartName, setSparePartName] = useState<string>(
		getArticleName(productItems, sparePart?.articleNumber) || '',
	);
	const [quantity, setQuantity] = useState<string>(sparePart?.quantity?.toString() || '');
	const [saleDate, setSaleDate] = useState<string>(getDateString(sparePart?.saleDate ?? null) || '');

	const debouncedFilterId = useDebounce(articleNumber, 500);

	const { data: installedEquipments, isLoading } = useInstalledEquipmentQuery(
		debouncedFilterId,
		settings?.data.searchSettings,
		!initialMount.current,
	);

	const installedEquipmentsOptions: SelectItem[] = useMemo(() => {
		if (!isLoading && installedEquipments) {
			if (installedEquipments.length > 0) {
				return installedEquipments.map((x) => {
					return {
						value: [x.id],
						label: x.id || '',
					};
				});
			}
			return [
				{
					value: [articleNumber],
					label: translations?.data.filterRegistrations.noItemFound || 'Not found',
				},
			];
		}
		return [];
	}, [installedEquipments, articleNumber, translations, isLoading]);

	const selectSparePart = (option: SelectItem) => {
		const selected = installedEquipments?.find((x) => x.id === option.value?.[0]);
		if (selected) {
			if (formId === 'register-sparePart-form') {
				setQuantity('1');
			}

			selected.name && setSparePartName(selected.name);
		} else {
			// Filter is not found but the user wants to use it anyway
			setSparePartName('');
			setQuantity('');
		}
		handleChange();
	};

	const modifySparePartId = (e) => {
		setArticleNumber(e.target.value);
		initialMount.current = false;
	};

	const modifySparePartName = (e) => {
		setSparePartName(e.target.value);
		handleChange();
	};

	const modifyQuantity = (e) => {
		setQuantity(e.target.value);
		handleChange();
	};

	const modifySaleDate = (e) => {
		setSaleDate(e.target.value);
		handleChange();
	};

	useEffect(() => {
		if (
			!initialMount.current &&
			installedEquipments &&
			articleNumber === sparePart?.articleNumber &&
			formId === 'edit-sparePart-form'
		) {
			const payload = {
				value: [sparePart?.articleNumber],
				label: getArticleName(productItems, sparePart?.articleNumber) || sparePart?.articleNumber || '',
			};
			selectSparePart(payload);
			setForceClose(true);
			window.setTimeout(() => {
				setForceClose(false);
			}, 5);
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [installedEquipments]);

	return (
		<form id={formId} onSubmit={handleSubmit}>
			<fieldset className={styles.fieldset}>
				<legend className="u-hide-visually">{legend}</legend>
				<InputText
					value={sparePartName}
					onChange={modifySparePartName}
					label={translations?.data.sparePartRegistrations.description}
					variant={'inverse'}
				/>
				<AutoComplete
					options={installedEquipmentsOptions}
					id={'articleNumber'}
					label={translations?.data.filterRegistrations.articleNo || 'Article No.'}
					onSelect={selectSparePart}
					onChange={modifySparePartId}
					name={'articleNumber'}
					value={articleNumber}
					variant={'inverse'}
					forceClose={forceClose}
				/>
				<InputText
					name="quantity"
					id="quantity"
					label={translations?.data.sparePartRegistrations.quantity}
					value={quantity}
					errorText={validationErrors?.Quantity?.[0]}
					onChange={modifyQuantity}
					variant={'inverse'}
					type="number"
					pattern="[0-9]"
				/>
				<InputText
					name="saleDate"
					id="saleDate"
					label={translations?.data.sparePartRegistrations.saleDate}
					value={saleDate}
					errorText={validationErrors?.SaleDate?.[0]}
					onChange={modifySaleDate}
					variant={'inverse'}
					type="date"
				/>
			</fieldset>
		</form>
	);
};
